import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FiAlertTriangle, FiCheckCircle, FiCheckSquare, FiExternalLink, FiLoader } from "react-icons/fi";
import { useHasInsuranceVerification } from "hooks/featureFlags";
import { useCurrentCompanyId } from "hooks/auth";
import { useValidateExistingDocument } from "../hooks/useValidateExistingDocument";
import { PendingVerificationBox } from "./PendingVerificationBox";
import { MonthlyCapBanner } from "./MonthlyCapBanner";
import { companyDocumentKeys } from "newui/features/Company/CompanyDocuments/companyDocumentKeys";
import type { CapReachedError } from "../api/insuranceVerificationApi";
import type { InsuranceOcrJobState } from "../types/InsuranceOcrJob.type";

interface ValidateHistoricalDocButtonProps {
	documentId: number;
	latestOcrJob: unknown | null;
	validated: boolean;
	/** Open the edit/review modal for this document */
	onViewResult?: () => void;
}

interface CapReachedErrorResponse {
	code: "cap_reached";
	resets_at: string;
}

type Phase = "idle" | "verifying" | "complete";

const isCapReachedError = (err: unknown): err is { response: { data: CapReachedError } } => {
	if (err !== null && typeof err === "object" && "response" in err) {
		const response = (err as { response: unknown }).response;
		if (response !== null && typeof response === "object" && "data" in response) {
			const data = (response as { data: unknown }).data;
			return (
				data !== null &&
				typeof data === "object" &&
				"code" in data &&
				(data as { code: unknown }).code === "cap_reached"
			);
		}
	}
	return false;
};

export const ValidateHistoricalDocButton: React.FC<ValidateHistoricalDocButtonProps> = ({
	documentId,
	latestOcrJob,
	validated,
	onViewResult,
}) => {
	const hasInsuranceVerification = useHasInsuranceVerification();
	const companyId = useCurrentCompanyId() as number;
	const queryClient = useQueryClient();
	const [capReachedData, setCapReachedData] = useState<CapReachedErrorResponse | null>(null);
	const [phase, setPhase] = useState<Phase>("idle");
	const [completedState, setCompletedState] = useState<InsuranceOcrJobState | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { mutate, isPending } = useValidateExistingDocument(documentId);

	if (!hasInsuranceVerification) {
		return null;
	}

	if (latestOcrJob !== null && latestOcrJob !== undefined) {
		return null;
	}

	if (validated) {
		return null;
	}

	const handleComplete = (newState: InsuranceOcrJobState) => {
		setCompletedState(newState);
		setPhase("complete");
		// Refresh the document list so the row updates
		queryClient.invalidateQueries({
			queryKey: companyDocumentKeys.documents(companyId),
		});
	};

	// Phase: verifying — show polling box
	if (phase === "verifying") {
		return (
			<PendingVerificationBox
				documentId={documentId}
				initialState="queued"
				onComplete={handleComplete}
			/>
		);
	}

	// Phase: complete — show result summary with link to view details
	if (phase === "complete" && completedState) {
		const isFailed = completedState === "failed";
		const isSoftFailed = completedState === "soft_failed";

		return (
			<div className="mt-1 flex items-center gap-2" data-testid={`validate-result-${documentId}`}>
				{isFailed ? (
					<>
						<FiAlertTriangle size={14} className="text-red-500" />
						<span className="text-xs text-red-600">
							Verification failed — file may be missing or corrupted.
						</span>
					</>
				) : (
					<>
						{isSoftFailed ? (
							<FiAlertTriangle size={14} className="text-amber-500" />
						) : (
							<FiCheckCircle size={14} className="text-green-500" />
						)}
						<span className="text-xs text-gray-600">
							{isSoftFailed ? "Partial results extracted." : "Verification complete."}
						</span>
					</>
				)}
				{!isFailed && onViewResult && (
					<button
						type="button"
						onClick={onViewResult}
						className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
					>
						<FiExternalLink size={12} />
						View Result
					</button>
				)}
			</div>
		);
	}

	if (capReachedData) {
		return (
			<MonthlyCapBanner
				used={0}
				limit={0}
				resetsAt={capReachedData.resets_at}
			/>
		);
	}

	const handleClick = () => {
		setErrorMessage(null);
		mutate(undefined, {
			onSuccess: () => {
				setPhase("verifying");
				// Refresh the document list so the row shows the active OCR state
				// with the inline PendingVerificationBox and "Processing" badge.
				queryClient.invalidateQueries({
					queryKey: companyDocumentKeys.documents(companyId),
				});
			},
			onError: (err: unknown) => {
				if (isCapReachedError(err)) {
					setCapReachedData(err.response.data as CapReachedErrorResponse);
				} else {
					setErrorMessage("Failed to start verification. Please try again.");
				}
			},
		});
	};

	return (
		<div data-testid={`validate-historical-doc-${documentId}`}>
			{errorMessage && (
				<p className="text-sm text-red-600" data-testid="validate-error-message">
					{errorMessage}
				</p>
			)}
			<button
				type="button"
				className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-200 transition-colors hover:bg-green-50 disabled:opacity-50"
				onClick={handleClick}
				disabled={isPending}
				data-testid={`validate-historical-doc-btn-${documentId}`}
				title="Verify document"
			>
				{isPending ? (
					<FiLoader size={14} className="animate-spin" />
				) : (
					<FiCheckSquare size={14} />
				)}
				{isPending ? "Verifying…" : "Verify"}
			</button>
		</div>
	);
};
